/**
 * SkillBuilderRail — the Skill Builder as a persistent side panel.
 *
 * Mounted in the app shell next to <Routes> (see AdminApp), NOT inside a
 * route, so it stays open while the merchant moves around the admin. That is
 * the whole point: the panel's answer ends in a "what you need to do next"
 * checklist, and every row of that checklist sends you to a different page.
 * Before this, following a step closed the panel and you arrived with no
 * memory of why you were there.
 *
 * State lives in context/SkillBuilderContext.jsx; this file is presentation
 * plus the navigation each checklist row performs.
 *
 * Three widths (docked / wide / full). Docked and wide are flex items in
 * .admin-layout, so the page shrinks beside them and stays usable. Full is a
 * fixed overlay over the content area — for reading a generated skill
 * document, where the page behind is not the point.
 *
 * Styling: styles/skill-builder.css for the rail shell; the thread and
 * composer reuse the `.skills-surface` families from styles/pages/skills.css
 * that the old dialog used.
 */

import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { activateSkill } from '../api/skills';
import {
  BUILD_MIN_CHARS,
  isPending,
  notifySkillsChanged,
  useSkillBuilder,
} from '../context/SkillBuilderContext';
import { Badge, Icon } from './skills/SkillsPrimitives';

/** Routes the rail stays out of: Conversations is a three-pane layout of its own. */
const HIDDEN_ON = ['/conversations'];

export const railHiddenFor = (pathname) =>
  HIDDEN_ON.some((p) => pathname === p || pathname.startsWith(`${p}/`));

/** Header button that opens the rail, with a dot when a build finished unseen. */
export function SkillBuilderLauncher() {
  const { t } = useTranslation('skills');
  const { open, togglePanel, unread, pending } = useSkillBuilder();
  return (
    <button
      type="button"
      className={`header-icon-btn ${open ? 'header-icon-btn--active' : ''}`}
      onClick={togglePanel}
      aria-label={t('builder.launcher')}
      aria-expanded={open}
      title={t('builder.launcher')}
    >
      <Icon name="bot" />
      {(unread || pending) && (
        <span
          className={`skill-rail__dot ${pending ? 'skill-rail__dot--busy' : ''}`}
          aria-hidden="true"
        />
      )}
    </button>
  );
}

export default function SkillBuilderRail() {
  const { t } = useTranslation('skills');
  const navigate = useNavigate();
  const location = useLocation();
  const {
    open, mode, setMode, closePanel,
    turns, pending, loading, sending, error, setError,
    draft, setDraft, send, startOver, skills, refreshSkills,
  } = useSkillBuilder();

  const inputRef = useRef(null);
  const endRef = useRef(null);
  const [flash, setFlash] = useState(null);
  // The skill id currently being activated, so a second click cannot fire a
  // second request while the first is still in flight.
  const [activating, setActivating] = useState(null);

  const hidden = railHiddenFor(location.pathname);

  useEffect(() => {
    if (!open || hidden) return;
    if (endRef.current) endRef.current.scrollIntoView({ block: 'end' });
  }, [turns, loading, open, hidden]);

  if (!open || hidden) return null;

  // First message of a thread has to describe a job; a follow-up can be
  // two words. The context enforces the same rule on send.
  const tooShort = draft.trim().length < (turns.length === 0 ? BUILD_MIN_CHARS : 1);
  const canSend = !tooShort && !sending && !pending;

  // The whole row, not just the id: a checklist row is rendered from a
  // result that was stored when the build finished, so the only way it can
  // tell that its step is already done is to look at the skill as it is NOW.
  const skillByName = (name) => (skills || []).find((s) => s.name === name) || null;

  /** Open the Skills page on a given skill's editor. The rail stays open. */
  const openSkill = (id) => {
    navigate(`/skills?skill=${id}`);
  };

  /**
   * Send the merchant to a section of the Skills page and scroll to it.
   * Navigating when we are already there is a no-op for the router, so the
   * scroll runs on the next frame either way.
   */
  const jumpTo = (sectionId) => {
    if (location.pathname !== '/skills') navigate('/skills');
    setTimeout(() => {
      const el = document.getElementById(sectionId);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 120);
  };

  const activate = async (id) => {
    if (activating !== null) return;
    setActivating(id);
    setError(null);
    try {
      const res = await activateSkill(id);
      setFlash(res?.runtime_enabled_now ? t('toast.runtimeEnabled') : t('toast.activated'));
      // Await the refresh: it is what flips this row from a button to
      // "Active", so the row must not settle before it lands.
      await refreshSkills();
      notifySkillsChanged();
    } catch (e) {
      setError(t('toast.activateFailed', { error: e.message }));
    } finally {
      setActivating(null);
    }
  };

  // An action per checklist row. All of them use paths that already exist.
  const stepAction = (step) => {
    switch (step.kind) {
      case 'activate_skill': {
        const skill = skillByName(step.target);
        if (!skill) return null;
        // Already live: the step is done, so say so instead of offering a
        // button that would re-activate what is already active.
        if (skill.status === 'active') {
          return (
            <span className="skill-rail__step-done">
              <Icon name="check" />
              {t('builder.stepDone')}
            </span>
          );
        }
        return (
          <>
            <button type="button" className="skills-btn skills-btn--ghost skills-btn--sm" onClick={() => openSkill(skill.id)}>
              {t('builder.review')}
            </button>
            <button
              type="button"
              className="skills-btn skills-btn--primary skills-btn--sm"
              onClick={() => activate(skill.id)}
              disabled={activating !== null}
            >
              {t('builder.activate')}
            </button>
          </>
        );
      }
      case 'deactivate_skill': {
        const skill = skillByName(step.target);
        if (!skill) return null;
        if (skill.status !== 'active') {
          return (
            <span className="skill-rail__step-done">
              <Icon name="check" />
              {t('builder.stepDone')}
            </span>
          );
        }
        return (
          <button type="button" className="skills-btn skills-btn--secondary skills-btn--sm" onClick={() => openSkill(skill.id)}>
            {t('builder.openSkill')}
          </button>
        );
      }
      case 'set_variable':
        return (
          <button type="button" className="skills-btn skills-btn--secondary skills-btn--sm" onClick={() => jumpTo('skills-variables')}>
            {t('builder.setValue')}
          </button>
        );
      case 'register_tool':
        return (
          <button type="button" className="skills-btn skills-btn--secondary skills-btn--sm" onClick={() => jumpTo('skills-tools')}>
            {t('builder.openTools')}
          </button>
        );
      case 'upload_knowledge':
        return (
          <button type="button" className="skills-btn skills-btn--secondary skills-btn--sm" onClick={() => navigate('/knowledge-base')}>
            {t('builder.openKnowledge')}
          </button>
        );
      case 'connect_integration':
        return (
          <button type="button" className="skills-btn skills-btn--secondary skills-btn--sm" onClick={() => navigate('/skills/integrations')}>
            {t('builder.connect')}
          </button>
        );
      case 'review': {
        const skill = skillByName(step.target);
        if (!skill) return null;
        return (
          <button type="button" className="skills-btn skills-btn--ghost skills-btn--sm" onClick={() => openSkill(skill.id)}>
            {t('builder.review')}
          </button>
        );
      }
      default:
        return null;
    }
  };

  const renderResult = (result) => (
    <>
      {result.incomplete && (
        <div className="skills-alert skills-alert--warning" role="alert">
          <Icon name="alert" />
          <span>{t('builder.incomplete')}</span>
        </div>
      )}
      <p className="skills-builder__summary">{result.summary}</p>

      {Array.isArray(result.skills) && result.skills.length > 0 && (
        <>
          <h3 className="skills-builder__section-title">{t('builder.skillsCreated')}</h3>
          <div className="skills-builder__chips">
            {result.skills.map((s) => (
              <Badge key={s.name} tone="neutral"><code className="skills__name">{s.name}</code></Badge>
            ))}
          </div>
        </>
      )}
      {Array.isArray(result.tools) && result.tools.length > 0 && (
        <>
          <h3 className="skills-builder__section-title">{t('builder.toolsCreated')}</h3>
          <div className="skills-builder__chips">
            {result.tools.map((x) => (
              <Badge key={x.tool_name} tone="neutral"><code className="skills__name">{x.tool_name}</code></Badge>
            ))}
          </div>
        </>
      )}
      {Array.isArray(result.variables) && result.variables.length > 0 && (
        <>
          <h3 className="skills-builder__section-title">{t('builder.variablesDeclared')}</h3>
          <div className="skills-builder__chips">
            {result.variables.map((v) => (
              <Badge key={v.name} tone={v.is_secret ? 'warning' : 'neutral'}>
                <code className="skills__name">{v.name}</code>
                {v.is_secret ? ` · ${t('builder.secret')}` : ''}
              </Badge>
            ))}
          </div>
        </>
      )}

      {Array.isArray(result.next_steps) && result.next_steps.length > 0 && (
        <>
          <h3 className="skills-builder__section-title">{t('builder.nextSteps')}</h3>
          <ol className="skills-builder__list">
            {result.next_steps.map((step, i) => (
              <li key={`${step.kind}-${step.target || i}`} className="skills-builder__item">
                <div className="skills-builder__item-main">
                  <span className="skills-builder__item-title">
                    <Badge tone={step.kind === 'activate_skill' ? 'success' : 'neutral'}>
                      {t(`builder.kind.${step.kind}`, { defaultValue: t('builder.kind.other') })}
                    </Badge>{' '}
                    {step.title}
                  </span>
                  {step.detail && <span className="skills-builder__item-detail">{step.detail}</span>}
                </div>
                <div className="skills-builder__actions">{stepAction(step)}</div>
              </li>
            ))}
          </ol>
        </>
      )}

      {Array.isArray(result.assumptions) && result.assumptions.length > 0 && (
        <>
          <h3 className="skills-builder__section-title">{t('builder.assumptions')}</h3>
          <ul className="skills-builder__assumptions">
            {result.assumptions.map((a, i) => (
              <li key={i}>{a}</li>
            ))}
          </ul>
        </>
      )}
    </>
  );

  return (
    <aside
      className={`skill-rail skill-rail--${mode} skills-surface`}
      aria-label={t('builder.launcher')}
    >
      <div className="skill-rail__head">
        <span className="skill-rail__title">
          <Icon name="bot" />
          {t('builder.launcher')}
        </span>
        <div className="skill-rail__head-actions">
          {/* One size control, not a widen + narrow pair: both arrows look
              alike, so two side by side read as two "expand" buttons. It
              steps docked -> wide -> full, then back to docked. */}
          <button
            type="button"
            className="skills-btn skills-btn--icon"
            onClick={() => setMode(mode === 'docked' ? 'wide' : mode === 'wide' ? 'full' : 'docked')}
            aria-label={mode === 'full' ? t('builder.narrow') : t('builder.widen')}
            title={mode === 'full' ? t('builder.narrow') : t('builder.widen')}
          >
            <Icon name={mode === 'full' ? 'collapse' : 'expand'} />
          </button>
          <button
            type="button"
            className="skills-btn skills-btn--icon"
            onClick={closePanel}
            aria-label={t('builder.close')}
            title={t('builder.close')}
          >
            <Icon name="close" />
          </button>
        </div>
      </div>

      <div className="skills-chat">
        <div className="skills-chat__thread">
          {loading && <span className="skills-skeleton" />}

          {!loading && turns.length === 0 && (
            <div className="skills-chat__intro">
              <p className="skills-builder__intro">{t('builder.intro')}</p>
              <span className="skills-builder__examples-label">{t('builder.examples')}</span>
              {['example1', 'example2', 'example3'].map((k) => (
                <button
                  key={k}
                  type="button"
                  className="skills-builder__example"
                  onClick={() => {
                    setDraft(t(`builder.${k}`));
                    if (inputRef.current) inputRef.current.focus();
                  }}
                >
                  {t(`builder.${k}`)}
                </button>
              ))}
            </div>
          )}

          {turns.map((turn) => (
            <div key={turn.id} className="skills-chat__turn">
              <div className="skills-chat__msg skills-chat__msg--user">{turn.requirement}</div>
              <div className="skills-chat__msg skills-chat__msg--bot">
                {isPending(turn) && (
                  <div className="skills-builder__status" role="status" aria-live="polite">
                    <span className="skills-spinner" aria-hidden="true" />
                    <div className="skills-builder__status-text">
                      <span>{t('builder.working')}</span>
                      <span className="skills-builder__status-hint">{t('builder.workingHintRail')}</span>
                    </div>
                  </div>
                )}
                {turn.status === 'failed' && (
                  <div className="skills-alert skills-alert--danger" role="alert">
                    <Icon name="alert" />
                    <span>{t('builder.failed', { error: turn.error || '' })}</span>
                  </div>
                )}
                {turn.status === 'done' && renderResult(turn.result || {})}
              </div>
            </div>
          ))}
          <div ref={endRef} />
        </div>

        {flash && (
          <div className="skills-alert skills-alert--success" role="status">
            <Icon name="check" />
            <span>{flash}</span>
            <button
              type="button"
              className="skills-btn skills-btn--icon"
              onClick={() => setFlash(null)}
              aria-label={t('builder.close')}
            >
              <Icon name="close" />
            </button>
          </div>
        )}

        {error && (
          <div className="skills-alert skills-alert--danger" role="alert">
            <Icon name="alert" />
            <span>{error}</span>
          </div>
        )}

        <div className="skills-chat__composer">
          <label className="skills__label" htmlFor="skills-builder-message">
            {turns.length === 0 ? t('builder.title') : t('builder.followUpLabel')}
          </label>
          <textarea
            id="skills-builder-message"
            ref={inputRef}
            className="skills-chat__input"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={turns.length === 0 ? t('builder.placeholder') : t('builder.followUpPlaceholder')}
            disabled={sending || Boolean(pending)}
          />
          <div className="skills-chat__composer-foot">
            <span className="skills__field-hint">
              {pending
                ? t('builder.waitForTurn')
                : tooShort && draft.trim().length > 0
                  ? t('builder.tooShort', { min: BUILD_MIN_CHARS })
                  : t('builder.draftsOnly')}
            </span>
            <div className="skill-rail__composer-actions">
              {turns.length > 0 && !pending && (
                <button type="button" className="skills-btn skills-btn--ghost" onClick={startOver}>
                  {t('builder.newConversation')}
                </button>
              )}
              <button
                type="button"
                className="skills-btn skills-btn--primary"
                onClick={() => send(draft, t)}
                disabled={!canSend}
              >
                {sending ? t('builder.working') : t('builder.send')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </aside>
  );
}
